import { discreteAvailabilityFunctions } from "../availabilityDiscrete";
import { getTimeLeftInDay, onDutyStatuses, type IState } from "../helpers";
import { AvailabilityCalculator, type TAvailabilityFactorEntry } from "./helpers/availabilityCalculator";
import { type TComputedAvailabilityResult } from "./helpers/shared";

export type DailyDutyConditions = {
    avail: number | undefined;
    isConsuming: boolean;
    hasActivity: boolean;
    isAtConfigMax: boolean;
};

export class DailyDutyCalculator extends AvailabilityCalculator {
    private conditions(state: IState): DailyDutyConditions {
        const avail = discreteAvailabilityFunctions.DailyDuty(state, false);
        const isConsuming = onDutyStatuses.indexOf(state.current?.status) !== -1;
        const hasActivity = state.day.onDutyCount > 0;
        const configMax = state.config.daily?.dutyMaximum;
        const isAtConfigMax = configMax !== undefined && avail === configMax;
        return { avail, isConsuming, hasActivity, isAtConfigMax };
    }

    extractConditions(state: IState): Record<string, unknown> {
        return this.conditions(state);
    }

    rawAvailability(state: IState): number | undefined {
        return this.conditions(state).avail;
    }

    isConsuming(state: IState): boolean {
        return this.conditions(state).isConsuming;
    }

    exhaustion(state: IState): Date | undefined {
        const { avail, isConsuming } = this.conditions(state);
        if (avail === undefined) return undefined;
        
        const timeLeftInDay = getTimeLeftInDay(state);
        
        // Guard avail > 0: raw value may be ≤ 0 when already in violation — no future
        // zero-crossing exists in that case.
        if (isConsuming && avail > 0 && avail < timeLeftInDay) {
            return new Date(state.nextDate.getTime() + avail);
        }
        return undefined;
    }

    availabilityFactors(state: IState): Record<string, TAvailabilityFactorEntry> | undefined {
        const c = this.conditions(state);
        if (c.avail === undefined) return undefined;

        // nextDayBoundary confirmed when consuming, when today already has activity, OR when
        // avail differs from the base config max (e.g. deferral extends the daily limit on DayOne,
        // or reduces it on DayTwo — both reset to the base at midnight). Suppressed only when
        // all three are false: !isConsuming, no activity today, AND avail === configMax.
        // In that case the discrete is already at the base maximum and midnight leaves it unchanged.
        const nextDayBoundary = (c.isConsuming || c.hasActivity || !c.isAtConfigMax)
            ? this.nextDayBoundary(state) : undefined;
            
        return {
            nextDayBoundary: { factor: () => nextDayBoundary, label: "day boundary" },
        };
    }
}

export const extractDailyDutyConditions = (state: IState): DailyDutyConditions => {
    return new DailyDutyCalculator().extractConditions(state) as DailyDutyConditions;
};

export const computeDailyDutyResult = (state: IState): TComputedAvailabilityResult | undefined => {
    return new DailyDutyCalculator().compute(state);
};
